import { useContext } from "react";
import { useLocalization } from "@fluent/react";
import {
  Drawer,
  Button,
  Row,
  Avatar,
  Tooltip,
  Chip,
  ContentCard,
} from "@narmi/design_system";
import Role from "byzantine/src/Role";
import { useRoleDetail } from "byzantine/src/Role/RolesProvider";
import OrganizationUser from "byzantine/src/OrganizationUser";
import {
  getUsersForRole,
  getExternalAccountSubtitle,
  hasAnyWritePermissionForLimits,
  DEFAULT_ROLE_NAMES,
  LIMITS_DATA,
} from "./OrganizationRoles.helpers";
import InstitutionSettingsContext from "../contexts/InstitutionSettingsContext";
import LongRoleTile from "./LongRoleTile";
import RoleDetailLoadingStatus from "./RoleDetailLoadingStatus";
import RoleAccountPermissionsView from "./RoleAccountPermissionsView";
import RoleLevelPermissionsView from "./RoleLevelPermissionsView";
import RoleLimitsView from "./RoleLimitsView";
import styles from "./OrganizationRoles.module.scss";

interface RoleDetailsDrawerProps {
  role: Role | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectRole: (role: Role) => void;
  organizationUsers?: OrganizationUser[];
  allAccounts?: (API.Account | API.ExternalAccount)[];
  orgLimits?: API.Limits.ByType;
  hasInstitutionLoaded?: boolean;
}

export function RoleDetailsDrawer({
  role,
  isOpen,
  onClose,
  onSelectRole,
  organizationUsers,
  allAccounts,
  orgLimits,
  hasInstitutionLoaded,
}: RoleDetailsDrawerProps) {
  const { l10n } = useLocalization();
  const institution = useContext(InstitutionSettingsContext);
  // Per-account permissions only come with the role detail.
  const {
    data: roleDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useRoleDetail(role?.uuid);

  if (!role) {
    return null;
  }

  const usersForRole = getUsersForRole(role.uuid, organizationUsers);
  const isDefault = DEFAULT_ROLE_NAMES.has(role.name);
  const isAdmin = role.name === OrganizationUser.ROLE.ADMIN;
  const isViewer = role.name === OrganizationUser.ROLE.VIEWER;

  // Compute dynamic subtitle for "Make external transfer" permission
  const externalTransferSubtitle = getExternalAccountSubtitle(
    role,
    allAccounts
  );

  const limits: API.Limits.ByType =
    Object.keys(role.limits).length > 0
      ? (role.limits as API.Limits.ByType)
      : orgLimits ?? {};

  const hasPermissionForAnyLimits = hasAnyWritePermissionForLimits(
    role.permissions,
    LIMITS_DATA
  );

  const handleSelectRole = () => {
    onSelectRole(role);
    onClose();
  };

  return (
    <Drawer
      isOpen={isOpen}
      onUserDismiss={onClose}
      depth="630px"
      paddingSize="l"
      showControls={false}
      footer={
        <div
          style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
        >
          <Button kind="secondary" onClick={onClose}>
            {l10n.getString("new-user-drawer-close")}
          </Button>
          <Button
            kind="primary"
            onClick={handleSelectRole}
            disabled={isDetailLoading}
          >
            {l10n.getString("new-user-drawer-select-role")}
          </Button>
        </div>
      }
    >
      <div style={{ paddingBottom: "20px" }}>
        <div style={{ marginBottom: "12px" }}>
          <Row gapSize="xs" alignItems="center">
            <h2 style={{ margin: 0 }}>{role.name}</h2>
            {isDefault && (
              <Chip
                isLabelVisible={false}
                startIcon="lock"
                label={l10n.getString("org-role-locked")}
              />
            )}
          </Row>
        </div>

        {role.description && (
          <p className={styles.roleDescription}>{role.description}</p>
        )}

        {usersForRole.length > 0 && (
          <>
            <p
              className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
              style={{ marginTop: "24px", marginBottom: "12px" }}
            >
              {l10n.getString("org-role-assigned-to")}
            </p>
            <ContentCard kind="bordered" paddingSize="m">
              {usersForRole.map((user, index) => (
                <div key={user.uuid}>
                  <Row gapSize="s" alignItems="center">
                    <Tooltip text={user.user.email}>
                      <Avatar
                        label={user.fullName()}
                        kind="tonal"
                        initials={user.userInitials()}
                      />
                    </Tooltip>
                    {user.fullName()}
                  </Row>
                  {index !== usersForRole.length - 1 && (
                    <hr className={styles.limitsdivider} />
                  )}
                </div>
              ))}
            </ContentCard>
          </>
        )}

        <RoleDetailLoadingStatus
          isLoading={isDetailLoading}
          isError={isDetailError && !roleDetail}
        />
        {roleDetail && (
          <>
            <div className={styles.cardSection}>
              <RoleAccountPermissionsView
                role={roleDetail}
                accounts={(allAccounts ?? []).filter(
                  (acc): acc is API.Account =>
                    !("externalAccountTypeName" in acc)
                )}
                institutionName={institution?.long_name}
                externalTransferSubtitle={externalTransferSubtitle}
                features={institution?.features}
                summaryTile={
                  (isAdmin && (
                    <LongRoleTile
                      iconClass="narmi-icon-check"
                      title={l10n.getString("org-role-admin-title")}
                      description={l10n.getString("org-role-admin-description")}
                    />
                  )) ||
                  (isViewer && (
                    <LongRoleTile
                      iconClass="narmi-icon-eye"
                      title={l10n.getString(
                        "org-role-viewer-all-accounts-title"
                      )}
                      description={l10n.getString(
                        "org-role-viewer-description"
                      )}
                    />
                  )) ||
                  undefined
                }
              />
            </div>
            {!isViewer && (
              <RoleLevelPermissionsView
                role={roleDetail}
                features={institution?.features}
                variant="card"
              />
            )}
          </>
        )}

        {hasInstitutionLoaded &&
          Object.keys(limits).length > 0 &&
          hasPermissionForAnyLimits && (
            <>
              <div
                className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
                style={{ marginTop: "24px", marginBottom: "12px" }}
              >
                {isAdmin
                  ? l10n.getString("org-role-organization-limits")
                  : l10n.getString("org-role-limits")}
              </div>
              <ContentCard kind="bordered" paddingSize="m">
                {isAdmin && (
                  <p
                    className={"fontSize--s"}
                    style={{
                      color: "var(--nds-color-gray-medium)",
                      marginBottom: "16px",
                    }}
                  >
                    {l10n.getString("org-role-organization-limits-subtitle")}
                  </p>
                )}
                <RoleLimitsView limits={limits} permissions={role.permissions} />
              </ContentCard>
            </>
          )}
      </div>
    </Drawer>
  );
}

export default RoleDetailsDrawer;
